import { Transition } from '@headlessui/react';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import ConfirmNameForm from './Slides/ConfirmNameForm/ConfirmNameForm';
import EndSlide from './Slides/EndSlide';
import ForcePasswordForm from './Slides/ForcePasswordForm/ForcePasswordForm';
import IntroSlide from './Slides/IntroSlide';
import UserPreferences from './Slides/UserPreferencesForm/UserPreferences';
import Steps from './Steps/Steps';

const allSlides = [IntroSlide, ConfirmNameForm, UserPreferences, ForcePasswordForm, EndSlide];

interface Props {
    initialSlide?: number;
    onClose?: () => void;
    // SSO users have no Vergo password to force a change on - their identity
    // is managed by the external IdP, so the mandatory password slide is
    // skipped rather than shown with nothing meaningful to change.
    skipPasswordSlide?: boolean;
}

const ScreenSlider = ({ initialSlide = 0, onClose, skipPasswordSlide = false }: Props) => {
    const slides = useMemo(
        () =>
            skipPasswordSlide ? allSlides.filter((slideComponent) => slideComponent !== ForcePasswordForm) : allSlides,
        [skipPasswordSlide],
    );

    const [isAnimating, setIsAnimating] = useState(false);
    const [slide, setSlide] = useState(Math.max(0, Math.min(initialSlide, slides.length - 1)));

    const handleNextSlide = () => {
        setIsAnimating(true);
        setSlide((slide: number) => slide + 1);
    };

    // I've done some weird md:p-2 stuff here to move the scrollbar on mobile and desktop so that the desktop has a padded in scrollbar and mobile is to the edge
    return (
        <section className="flex flex-col max-h-full pt-2 md:p-2 space-y-2">
            <header className="flex-grow-0 flex-shrink-0 flex flex-col items-center space-y-2">
                <Image src="/logos/vergo-logo-dark.svg" alt="Vergo hub logo" width={100} height={100} />
                <Steps steps={slides} currentStep={slide} />
            </header>
            <div className={`flex-grow w-full ${isAnimating ? 'overflow-hidden' : 'overflow-auto'}`}>
                <div className="relative w-full max-w-md mx-auto p-2 md:p-0">
                    {slides.map((_, index) => (
                        <Transition
                            as="div"
                            // TODO: index needs to be unique.
                            key={index} // eslint-disable-line react/no-array-index-key
                            show={slide === index}
                            appear={slide > 0}
                            enter="transition-[max-height,opacity] overflow-hidden ease-in-out motion-safe:duration-[400ms]"
                            enterFrom="max-h-[0vh] opacity-0"
                            enterTo="max-h-[100dvh] opacity-100"
                            leave="transition-[max-height,opacity] overflow-hidden ease-in-out motion-safe:duration-[400ms]"
                            leaveFrom="max-h-[100dvh] opacity-100"
                            leaveTo="max-h-[0dvh] opacity-0"
                            className="w-full duration-0"
                            afterEnter={() => setIsAnimating(false)}
                        >
                            <div className="flex-shrink-0 w-full">
                                {slides[index]({ nextSlide: handleNextSlide, onClose })}
                            </div>
                        </Transition>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ScreenSlider;
